import React, { useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import JsonInput, { JsonInputRef } from './components/JsonInput';
import PathDisplay from './components/PathDisplay';
import SearchBar from './components/SearchBar';
import Toast from './components/Toast';
import SEOHead from './components/SEOHead';
import LanguageSwitcher from './components/LanguageSwitcher';
import ThemeToggle from './components/ThemeToggle';
import { extractJsonPaths, PathNode } from './utils/jsonPathUtils';
import { useDebounce } from './utils/searchUtils';

function App() {
  const { t } = useTranslation();
  const [paths, setPaths] = useState<PathNode[]>([]);
  const [selectedPath, setSelectedPath] = useState<string | null>(null);
  const [toastVisible, setToastVisible] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isRegexSearch, setIsRegexSearch] = useState(false);
  const [enableJSON5, setEnableJSON5] = useState(false);
  const jsonInputRef = useRef<JsonInputRef>(null);
  
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  const handleJsonChange = useCallback((json: any) => {
    if (json) {
      const extractedPaths = extractJsonPaths(json);
      setPaths(extractedPaths);
    } else {
      setPaths([]);
    }
    setSelectedPath(null);
  }, []);

  const handleError = useCallback((error: string | null) => {
    if (error) {
      setPaths([]);
      setSelectedPath(null);
    }
  }, []);

  const handlePathSelect = useCallback(async (path: string) => {
    setSelectedPath(path);
    
    // Format the path with $ prefix for JSONPath syntax
    // Handle array indices and object properties correctly
    let fullPath = '$';
    if (path && path !== '') {
      // If path starts with [, it's an array index at root level
      if (path.startsWith('[')) {
        fullPath = `$${path}`;
      } else {
        fullPath = `$.${path}`;
      }
    }
    
    try {
      await navigator.clipboard.writeText(fullPath);
      setToastVisible(true);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  }, []);

  const handleToastClose = useCallback(() => {
    setToastVisible(false);
  }, []);

  const handleSearch = useCallback((query: string, isRegex: boolean) => {
    setSearchQuery(query);
    setIsRegexSearch(isRegex);
  }, []);

  const handleReset = useCallback(() => {
    setPaths([]);
    setSelectedPath(null);
    setToastVisible(false);
    setSearchQuery('');
    setIsRegexSearch(false);
    jsonInputRef.current?.reset();
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-bg-light dark:bg-dark-bg font-primary">
      <SEOHead />
      
      <header className="bg-bg-white dark:bg-dark-bg-secondary shadow-light border-b border-border-light dark:border-dark-border">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-6">
          <div className="flex flex-col space-y-4 sm:flex-row sm:justify-between sm:items-start sm:space-y-0">
            <div className="flex-1">
              <button 
                onClick={handleReset}
                className="text-2xl sm:text-3xl font-logo font-semibold text-primary-dark dark:text-dark-text hover:text-primary-blue dark:hover:text-dark-primary transition-colors duration-200 cursor-pointer mb-2 block"
              >
                {t('header.title')}
              </button>
              <p className="text-sm sm:text-base text-text-gray dark:text-dark-text-secondary font-primary">
                {t('header.subtitle')}
              </p>
            </div>
            
            <div className="flex flex-wrap items-center gap-2 sm:gap-4">
              <div className="flex items-center gap-2 sm:gap-4">
                <LanguageSwitcher />
                <ThemeToggle />
              </div>
              
              <div className="flex items-center gap-2 order-first sm:order-none w-full sm:w-auto">
                <label className="flex items-center gap-2 text-xs sm:text-sm text-text-gray dark:text-dark-text-secondary cursor-pointer">
                  <input
                    type="checkbox"
                    checked={enableJSON5}
                    onChange={(e) => setEnableJSON5(e.target.checked)}
                    className="w-4 h-4 text-primary-blue bg-gray-100 border-gray-300 rounded focus:ring-primary-blue focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                  />
                  <span className="whitespace-nowrap">{t('header.json5Support')}</span>
                </label>
              </div>
              
              <div className="hidden sm:block text-xl sm:text-2xl font-logo font-bold text-primary-blue dark:text-dark-primary">
                LEANOFY
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8 w-full">
        <div className={`flex flex-col lg:grid lg:grid-cols-2 gap-4 sm:gap-8 ${paths.length > 0 ? 'min-h-[600px]' : 'min-h-[calc(100vh-280px)]'}`}>
          <div className="bg-bg-white dark:bg-dark-bg-secondary rounded-lg shadow-medium border border-border-light dark:border-dark-border p-3 sm:p-6 min-h-[300px] sm:min-h-[400px] order-1 lg:order-none">
            <JsonInput 
              ref={jsonInputRef} 
              onJsonChange={handleJsonChange} 
              onError={handleError}
              selectedPath={selectedPath}
              enableJSON5={enableJSON5}
            />
          </div>
          
          <div className="bg-bg-white dark:bg-dark-bg-secondary rounded-lg shadow-medium border border-border-light dark:border-dark-border p-3 sm:p-6 min-h-[300px] sm:min-h-[400px] order-2 lg:order-none">
            <div className="mb-4 sm:mb-6">
              <h2 className="text-lg sm:text-xl font-logo font-semibold text-primary-dark dark:text-dark-text mb-2">{t('jsonPaths.title')}</h2>
              <p className="text-sm sm:text-base text-text-gray dark:text-dark-text-secondary font-primary">{t('jsonPaths.subtitle')}</p>
            </div>
            
            {paths.length > 0 && (
              <SearchBar onSearch={handleSearch} />
            )}
            
            <PathDisplay 
              paths={paths} 
              onPathSelect={handlePathSelect}
              selectedPath={selectedPath}
              searchQuery={debouncedSearchQuery}
              isRegexSearch={isRegexSearch}
            />
          </div>
        </div>

        {selectedPath && (
          <div className="mt-4 sm:mt-8 bg-bg-white dark:bg-dark-bg-secondary rounded-lg shadow-medium border border-border-light dark:border-dark-border p-3 sm:p-6">
            <h3 className="text-base sm:text-lg font-medium text-primary-dark dark:text-dark-text mb-3 font-logo">{t('selectedPath.title')}</h3>
            <div className="bg-bg-light dark:bg-dark-bg rounded-lg p-3 sm:p-4 font-mono text-xs sm:text-sm text-text-dark dark:text-dark-text border border-border-light dark:border-dark-border break-all">
              {(() => {
                if (!selectedPath || selectedPath === '') return '$';
                if (selectedPath.startsWith('[')) return `$${selectedPath}`;
                return `$.${selectedPath}`;
              })()}
            </div>
            <p className="text-text-gray dark:text-dark-text-secondary mt-3 text-xs sm:text-sm">
              {t('selectedPath.copied')}
            </p>
          </div>
        )}
      </main>

      <Toast 
        message={t('toast.pathCopied')}
        isVisible={toastVisible}
        onClose={handleToastClose}
      />

      <footer className="bg-bg-white dark:bg-dark-bg-secondary border-t border-border-light dark:border-dark-border mt-auto">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-6">
          <div className="flex flex-col sm:flex-row justify-between items-center">
            <div className="text-text-gray dark:text-dark-text-secondary font-primary text-xs sm:text-sm mb-4 sm:mb-0 text-center sm:text-left">
              © 2025 <span className="font-logo font-medium text-primary-dark dark:text-dark-text">LEANOFY</span>. {t('footer.copyright')}
            </div>
            <div className="flex space-x-4 sm:space-x-6">
              <a 
                href="https://leanofy.de/datenschutz" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-text-gray dark:text-dark-text-secondary hover:text-primary-blue dark:hover:text-dark-primary transition-colors duration-200 font-primary text-xs sm:text-sm"
              >
                {t('footer.privacy')}
              </a>
              <a 
                href="https://leanofy.de/impressum" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-text-gray dark:text-dark-text-secondary hover:text-primary-blue dark:hover:text-dark-primary transition-colors duration-200 font-primary text-xs sm:text-sm"
              >
                {t('footer.imprint')}
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
